import i18n from "i18next";
import { ConfigurableObjectType } from "src/api/generated/erp/db/types/enums/configurableObjectType.ts";
import type { OpenGenericDialogFunc } from "src/components/common/dialogs/GenericDialogContext.ts";
import { showAsyncDialog } from "src/components/common/dialogs/asyncDialog.ts";
import { ObjectConfigurationHistoryEntryInputForm } from "src/components/views/erp/configurator/objectConfigurationHistory/ObjectConfigurationHistoryEntryInputForm.tsx";
import { ObjectConfigurationHistoryEntryInput } from "src/api/generated/erp/configurator/objectConfigurationHistory/objectConfigurationHistoryEntryInput.ts";
import { ObjectConfigurationHistoryApi } from "src/api/generated/erp/configurator/objectConfigurationHistory/api/objectConfigurationHistoryApi.ts";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { ConfiguratorInput } from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput.ts";
import { ConfigurationCheckpointState } from "src/components/views/erp/configurator/configuratorForm/checkpoints/configurationCheckpoints.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

interface AskObjectConfigurationHistoryEntryInputProps {
	openDialog: OpenGenericDialogFunc;
	objectType: ConfigurableObjectType;
	objectId: number;
	configurationSessionId: string;
	configuratorType: PartConfiguratorType;
	configuratorInput: ConfiguratorInput;
	checkpointState: ConfigurationCheckpointState;
}

export async function askObjectConfigurationHistoryEntryInput({
	openDialog,
	objectType,
	objectId,
	configurationSessionId,
	configuratorType,
	configuratorInput,
	checkpointState,
}: AskObjectConfigurationHistoryEntryInputProps): Promise<ObjectConfigurationHistoryEntryInput | undefined> {
	const hasHistoryEntries = await ObjectConfigurationHistoryApi.getObjectConfigurationHistoryHasAnyEntry({
		objectType,
		objectId,
	});
	// No reason to bother the user with the dialog if there are no history entries
	if (!hasHistoryEntries) {
		return {
			keepCurrentEntry: true,
			comment: "",
		};
	}

	const result: ObjectConfigurationHistoryEntryInput | undefined = await showAsyncDialog(
		openDialog,
		({ onCompleted, onFormEdited }) => ({
			title: i18n.t("configuration_history"),
			size: "sm",
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.objectConfigurationHistory.entryInput.dialog"),
				data: { objectType, objectId, configurationSessionId },
				pathSegments: [i18n.t("configuration_history")],
			},
			content: (
				<ObjectConfigurationHistoryEntryInputForm
					objectType={objectType}
					objectId={objectId}
					configurationSessionId={configurationSessionId}
					configuratorType={configuratorType}
					configuratorInput={configuratorInput}
					checkpointState={checkpointState}
					onCompleted={onCompleted}
					onFormEdited={onFormEdited}
				/>
			),
		}),
	);

	return result;
}
